/**
 * Browser-side Sentry sink. Initialized from `src/routes/__root.tsx`.
 *
 * DSN is a publishable value — safe to ship in the client bundle via
 * `VITE_SENTRY_DSN`. If the env var is missing, the sink is a no-op.
 */
import * as Sentry from "@sentry/react";

type Extras = Record<string, unknown> | undefined;

let initialized = false;

const PII_KEY_RX = /email|password|token|secret|api[_-]?key|authorization|cookie/i;

function scrubExtras(extras: Extras): Extras {
  if (!extras) return extras;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(extras)) {
    out[k] = PII_KEY_RX.test(k) ? "[redacted]" : v;
  }
  return out;
}

export function initClientSentry() {
  if (initialized || typeof window === "undefined") return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  try {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ?? import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1"),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR ?? "0"),
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
      beforeSend(event) {
        if (event.request?.headers) {
          const h = event.request.headers as Record<string, string>;
          delete h.authorization;
          delete h.Authorization;
          delete h.cookie;
          delete h.Cookie;
        }
        if (event.extra) event.extra = scrubExtras(event.extra) as Record<string, unknown>;
        return event;
      },
    });
    initialized = true;
  } catch {
    /* swallow */
  }
}

export const clientSentrySink = {
  captureException(err: unknown, extras?: Extras) {
    if (!initialized) return;
    try {
      Sentry.captureException(err, { extra: scrubExtras(extras) as Record<string, unknown> | undefined });
    } catch {
      /* swallow */
    }
  },
  captureMessage(msg: string, extras?: Extras) {
    if (!initialized) return;
    try {
      Sentry.captureMessage(msg, {
        level: "warning",
        extra: scrubExtras(extras) as Record<string, unknown> | undefined,
      });
    } catch {
      /* swallow */
    }
  },
};